import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import ErrorPage from '@/components/shared/ErrorPage';
import { constants } from '@/constants';
import ContactInfo from '@/features/appointments/routes/ContactInfo';
import EmailInquiry from '@/features/appointments/routes/EmailInquiry';
import ScheduleAppointment from '@/features/appointments/routes/ScheduleAppointment';
import AppointmentCancelled from '@/features/confirmation/components/AppointmentCancelled';
import Confirmation from '@/features/confirmation/routes/Confirmation';
import EmailConfirmation from '@/features/confirmation/routes/EmailConfirmation';
import Destinations from '@/features/destinations/routes/Destinations';
import LoadAppointment from '@/features/loadAppointment/routes/LoadAppointment';
import RelevanceSearch from '@/features/relevanceSearch/routes/RelevanceSearch';
import type { FeatureFlags } from '@/hooks/useFeatureFlags';
import { FlowType } from '@/types/FlowType';

const baseName = constants.APP_BASE_URL;

const location = window.location.pathname;
const emailFlowPaths = ['/email', '/email-inquiry', '/email-confirmation'];

const isEmailFlow = emailFlowPaths.some((path) => location.startsWith(path));

const redirectPath = isEmailFlow ? `${constants.APP_BASE_URL}/email` : baseName;

interface RoutesOptions {
  isLuxury?: boolean;
  featureFlags: FeatureFlags;
}

export const createRoutesConfiguration = ({
  isLuxury = false,
  featureFlags,
}: RoutesOptions): RouteObject[] => {
  const showRelevanceSearch = featureFlags.relevanceSearch && !isLuxury;

  return [
    {
      path: '/',
      element: showRelevanceSearch ? (
        <RelevanceSearch flow={FlowType.SCHEDULE} />
      ) : (
        <Destinations flow={FlowType.SCHEDULE} />
      ),
    },
    {
      path: '/email',
      element: showRelevanceSearch ? (
        <RelevanceSearch flow={FlowType.EMAIL} />
      ) : (
        <Destinations flow={FlowType.EMAIL} />
      ),
    },
    {
      path: '/schedule-appointment/:id',
      element: <ScheduleAppointment />,
    },
    {
      path: '/schedule-appointment/:id/contact-info',
      element: <ContactInfo />,
    },
    { path: '/confirmation', element: <Confirmation /> },
    { path: '/load-appointment', element: <LoadAppointment /> },
    {
      path: '/email-inquiry/:id/contact-info',
      element: <EmailInquiry />,
    },
    {
      path: '/email-confirmation',
      element: <EmailConfirmation />,
    },
    {
      path: '/reschedule-appointment/:id',
      element: <ScheduleAppointment />,
    },
    {
      path: '/cancel-appointment/:id',
      element: <AppointmentCancelled />,
    },
    {
      path: '/*',
      element: (
        <ErrorPage
          onClickTryAgain={() => {
            window.location.replace(redirectPath);
          }}
        />
      ),
    },
  ];
};

export const createRouter = (isLuxury: boolean, featureFlags: FeatureFlags) => {
  const routesConfiguration = createRoutesConfiguration({
    isLuxury,
    featureFlags,
  });
  return createBrowserRouter(routesConfiguration, {
    basename: isLuxury
      ? `${constants.APP_BASE_URL}/luxury`
      : constants.APP_BASE_URL,
  });
};
